import { useCallback, useEffect, useState } from "react";

async function api(path, options) {
  return fetch(path, {
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
}

const ROLE_STATUSES = ["open", "filled", "closed"];
const INTEREST_STATUSES = ["new", "contacted", "accepted", "declined"];

const SUCCESSION_BADGES = {
  overdue: { label: "Overdue", color: "#b42318", bg: "#fdf3f2" },
  due_soon: { label: "Due soon", color: "#8a6d00", bg: "#fdf6dd" },
  ok: { label: "OK", color: "var(--green)", bg: "var(--green-tint)" },
  unset: { label: "No end date", color: "var(--muted)", bg: "var(--surface-2)" },
};

function SuccessionBadge({ status }) {
  const badge = SUCCESSION_BADGES[status];
  return (
    <span
      className="pill"
      style={{ color: badge.color, background: badge.bg, borderColor: "transparent" }}
    >
      {badge.label}
    </span>
  );
}

function Succession({ terms, canManage, reload }) {
  async function setTermEnd(term, value) {
    if ((term.term_ends_on || "") === value) return;
    await api(`/api/board/${term.id}`, {
      method: "PATCH",
      body: JSON.stringify({ term_ends_on: value }),
    });
    reload();
  }

  return (
    <div className="section">
      <div className="section-head">
        <h3>Succession</h3>
        <span className="muted" style={{ fontSize: 13 }}>
          When each current officer's term is up for re-election.
        </span>
      </div>
      {terms.length ? (
        <table className="tbl">
          <thead>
            <tr>
              <th>Office</th>
              <th>Holder</th>
              <th>Serving since</th>
              <th>Term ends</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {terms.map((t) => (
              <tr key={t.id}>
                <td>{t.office}</td>
                <td>{t.holder_name}</td>
                <td>{t.started_on}</td>
                <td>
                  {canManage ? (
                    <input
                      type="date"
                      name={`term-ends-${t.id}`}
                      defaultValue={t.term_ends_on || ""}
                      onBlur={(e) => setTermEnd(t, e.target.value)}
                      style={{ width: "auto", padding: "4px 8px" }}
                    />
                  ) : (
                    t.term_ends_on || "—"
                  )}
                </td>
                <td>
                  <SuccessionBadge status={t.succession_status} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">No current officers — add the board on the Overview tab.</p>
      )}
    </div>
  );
}

function NewRoleForm({ chapterId, onCreated }) {
  const empty = { title: "", commitment: "", status: "open", description: "" };
  const [form, setForm] = useState(empty);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setError("");
    const res = await api(`/api/chapters/${chapterId}/volunteers/roles`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setForm(empty);
      onCreated();
    } else {
      setError((await res.json().catch(() => ({}))).error || "Could not create the role.");
    }
  }

  return (
    <form className="form-card" onSubmit={submit}>
      <div className="form-grid">
        <label className="field">
          Role title
          <input name="title" required value={form.title} onChange={set("title")} placeholder="Events coordinator" />
        </label>
        <label className="field">
          Commitment
          <input name="commitment" value={form.commitment} onChange={set("commitment")} placeholder="2-4 hrs/month" />
        </label>
        <label className="field">
          Status
          <select name="status" value={form.status} onChange={set("status")}>
            {ROLE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          Description
          <input name="description" value={form.description} onChange={set("description")} placeholder="What the role involves" />
        </label>
        <button className="btn-primary" style={{ width: "auto" }} type="submit">
          Add role
        </button>
      </div>
      {error && <div className="err">{error}</div>}
    </form>
  );
}

function AddInterestForm({ role, reload }) {
  const [form, setForm] = useState({ name: "", email: "", note: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    const res = await api(`/api/volunteers/roles/${role.id}/interests`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setForm({ name: "", email: "", note: "" });
      reload();
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
      <input name="name" required placeholder="Name" value={form.name} onChange={set("name")} style={{ width: 160 }} />
      <input name="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} style={{ width: 200 }} />
      <input name="note" placeholder="Note" value={form.note} onChange={set("note")} style={{ flex: 1, minWidth: 140 }} />
      <button className="btn-ghost" type="submit">
        Record interest
      </button>
    </form>
  );
}

function Interests({ role, canManage, reload }) {
  async function setStatus(interest, status) {
    await api(`/api/volunteers/interests/${interest.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    reload();
  }

  async function accept(interest) {
    await api(`/api/volunteers/interests/${interest.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "accepted" }),
    });
    await api(`/api/volunteers/roles/${role.id}`, {
      method: "PATCH",
      body: JSON.stringify(
        interest.member_id
          ? { holder_member_id: interest.member_id, status: "filled" }
          : { holder_name: interest.name, status: "filled" }
      ),
    });
    reload();
  }

  async function remove(interest) {
    await api(`/api/volunteers/interests/${interest.id}`, { method: "DELETE" });
    reload();
  }

  return (
    <>
      {role.interests.length ? (
        <table className="tbl" style={{ marginTop: 8 }}>
          <tbody>
            {role.interests.map((i) => (
              <tr key={i.id}>
                <td>
                  {i.name}
                  {i.email && (
                    <span className="muted" style={{ fontSize: 13 }}>
                      {" "}
                      · {i.email}
                    </span>
                  )}
                  {i.note && (
                    <div className="muted" style={{ fontSize: 13 }}>
                      {i.note}
                    </div>
                  )}
                </td>
                <td style={{ width: 130 }}>
                  {canManage ? (
                    <select
                      name={`interest-status-${i.id}`}
                      value={i.status}
                      onChange={(e) => setStatus(i, e.target.value)}
                      style={{ padding: "4px 8px" }}
                    >
                      {INTEREST_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="pill">{i.status}</span>
                  )}
                </td>
                {canManage && (
                  <td className="row-actions">
                    {i.status !== "accepted" && role.status === "open" && (
                      <button className="btn-ghost" onClick={() => accept(i)} type="button">
                        Accept &amp; fill role
                      </button>
                    )}
                    <button className="btn-danger" onClick={() => remove(i)} type="button">
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="empty">No expressions of interest yet.</p>
      )}
      {canManage && <AddInterestForm role={role} reload={reload} />}
    </>
  );
}

function RoleRow({ role, canManage, reload, expanded, onToggle }) {
  async function setStatus(status) {
    await api(`/api/volunteers/roles/${role.id}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    });
    reload();
  }

  async function remove() {
    if (!window.confirm(`Delete the "${role.title}" role and its interests?`)) return;
    await api(`/api/volunteers/roles/${role.id}`, { method: "DELETE" });
    reload();
  }

  return (
    <>
      <tr>
        <td>
          <strong>{role.title}</strong>
          {role.commitment && (
            <span className="muted" style={{ fontSize: 13 }}>
              {" "}
              · {role.commitment}
            </span>
          )}
          {role.description && (
            <div className="muted" style={{ fontSize: 13 }}>
              {role.description}
            </div>
          )}
        </td>
        <td>{role.holder_name || <span className="muted">—</span>}</td>
        <td style={{ width: 110 }}>
          {canManage ? (
            <select
              name={`role-status-${role.id}`}
              value={role.status}
              onChange={(e) => setStatus(e.target.value)}
              style={{ padding: "4px 8px" }}
            >
              {ROLE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          ) : (
            <span className="pill">{role.status}</span>
          )}
        </td>
        <td className="row-actions">
          <button className="btn-ghost" onClick={onToggle} type="button">
            {expanded ? "Hide" : `Interest (${role.interest_count})`}
          </button>
          {canManage && (
            <button className="btn-danger" onClick={remove} type="button">
              Delete
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={4} style={{ background: "var(--surface-2)" }}>
            <Interests role={role} canManage={canManage} reload={reload} />
          </td>
        </tr>
      )}
    </>
  );
}

export function Volunteers({ chapterId, canManage }) {
  const [data, setData] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const reload = useCallback(async () => {
    const res = await api(`/api/chapters/${chapterId}/volunteers`);
    if (res.ok) setData(await res.json());
  }, [chapterId]);

  useEffect(() => {
    reload();
  }, [reload]);

  if (data === null) return <p className="muted">Loading…</p>;

  const open = data.roles.filter((r) => r.status === "open").length;

  return (
    <>
      <Succession terms={data.succession} canManage={canManage} reload={reload} />

      <div className="section">
        <div className="section-head">
          <h3>Volunteer roles</h3>
          <span className="muted" style={{ fontSize: 13 }}>
            {open ? `${open} open position${open === 1 ? "" : "s"}` : "No open positions"} —
            open roles are offered to members in their account area.
          </span>
        </div>
        {data.roles.length ? (
          <table className="tbl">
            <thead>
              <tr>
                <th>Role</th>
                <th>Holder</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.roles.map((r) => (
                <RoleRow
                  key={r.id}
                  role={r}
                  canManage={canManage}
                  reload={reload}
                  expanded={expandedId === r.id}
                  onToggle={() => setExpandedId(expandedId === r.id ? null : r.id)}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <p className="empty">No volunteer roles yet — add the positions your chapter needs.</p>
        )}
        {canManage && <NewRoleForm chapterId={chapterId} onCreated={reload} />}
      </div>
    </>
  );
}
